// Quotes from Amanda's own prices (live hotfix Oct 20, 2026; folded into Phase 11 as Step 5).
// Packages and add-ons come from Settings (Phase 11's packages.js, defaults from her pricing sheet).
// The couple's chosen add-ons are always included at her rates. The AI only picks the package when the
// couple asked for help, suggests add-ons their answers call for, sets the Full-Service fee (never below
// its starting price), and prices custom extras against the Central Coast market-rate table in Settings.

import { normalizeSettings, packageIdForTier as tierToPackage } from "../../supabase/functions/_shared/packages.js";
import { DEFAULT_MARKET_RATES } from "./market-rates.js";

export const packageIdForTier = tierToPackage;

// What each package leaves out (pricing sheet and contract Section 3), so the AI knows what counts as an extra.
const NOT_INCLUDED = {
  "wedding-day": "Vendor sourcing, building the wedding day timeline, rehearsal, planning meetings, design and styling, budget management",
  "partial": "Design and styling, wedding budget management, full vendor sourcing for every category",
  "full-service": "Anything beyond the inclusions above is an add-on or a custom extra"
};

const money = v => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? Number(v.replace(/[$,\s]/g, "")) : Number(v);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : null;
};
const usd = n => "$" + Number(n).toLocaleString("en-US");

/** Her packages (with the inclusions a package inherits), add-ons and market table, from Settings. */
export function pricingFrom(settings) {
  const s = normalizeSettings(settings);
  const byId = id => s.packages.find(p => p.id === id);
  const packages = s.packages.map(p => {
    const parent = p.includes ? byId(p.includes) : null;
    return {
      ...p,
      allInclusions: [...(parent ? parent.inclusions : []), ...(p.inclusions || [])],
      notIncluded: NOT_INCLUDED[p.id] || ""
    };
  });
  const mr = s.marketRates && Array.isArray(s.marketRates.rows) ? s.marketRates : DEFAULT_MARKET_RATES;
  return { packages, addons: s.addons, marketRates: mr };
}

export const packagePriceLabel = p => p.price !== null && p.price !== undefined ? usd(p.price)
  : p.priceFrom ? "from " + usd(p.priceFrom) + " (custom)" : "quoted case by case";

const unitLabel = u => u === "hour" ? "per hour" : u === "person" ? "per person" : "flat";

export function marketRatesText(mr) {
  const rows = (mr && mr.rows) || [];
  if (!rows.length) return "(No market-rate table saved. Say so in each custom line's market note and price conservatively.)";
  const range = r => {
    const lo = money(r.low), hi = money(r.high);
    if (lo !== null && hi !== null && lo !== hi) return "$" + lo.toLocaleString("en-US") + "–$" + hi.toLocaleString("en-US");
    const v = lo !== null ? lo : hi;
    return v !== null ? "$" + v.toLocaleString("en-US") : "no figure";
  };
  return rows.map(r => "- " + r.item + ": " + range(r) + " " + (r.unit || "") + (r.note ? " (" + r.note + ")" : "")).join("\n")
    + ((mr.region || mr.asOf) ? "\n(Region: " + (mr.region || "Central Coast") + (mr.asOf ? "; collected " + mr.asOf : "") + ")" : "");
}

export const QUOTE_SYSTEM = [
  "You help a one-woman wedding coordination studio on California's Central Coast price a proposal for one couple.",
  "Her packages and add-ons have fixed prices that you never change. Your job is narrow:",
  "(1) pick her package: use the couple's requested package; recommend one only if they asked for help choosing or gave none;",
  "(2) add any of her add-ons their answers call for beyond the ones they already chose, with a quantity for hourly or per-person add-ons;",
  "(3) for a package quoted case by case, set the fee at or above its starting price, scaled to guest count, timeline and scope;",
  "(4) price any CUSTOM EXTRAS: things the couple asked for that her package does not include and no add-on covers",
  "(for example a welcome party, a farewell brunch, a second ceremony, design help on a non-design package, multi-day coverage).",
  "Price each custom extra against the Central Coast market-rate table you are given, never national averages.",
  "Never add a custom extra for something the package already includes or an add-on covers. If nothing calls for one, return none.",
  "Return STRICT JSON only, no prose and no markdown fences, in this shape:",
  '{"packageId": string, "customFee": number|null, "addons": [{"id": string, "qty": number, "note": string}], "custom": [{"label": string, "amount": number, "note": string, "market": string}], "rationale": string}',
  "Amounts are whole US dollars. qty is hours for per-hour add-ons, people for per-person add-ons, and 1 for flat ones.",
  "market: one or two sentences naming the table rows you compared against and the range they give. It is internal and never shown to the couple.",
  "rationale: two or three sentences on the package choice and anything you added, tied to the couple's answers. It is internal and never shown to the couple.",
  "Labels and notes may be shown to the couple, so write them in the studio's voice: plain, warm, a little dry. No exclamation points, no hype, never 'dream wedding', 'magical' or 'princess'.",
  "The studio's own upfront payment is a 'retainer', never a 'deposit'."
].join(" ");

/** chosen: { ids: [...], qty: { id: n } } — the add-ons the couple picked on their profile or questionnaire. */
export function buildQuotePrompt(clientSummary, requestedPackageId, pricing, chosen) {
  const L = [];
  L.push("HER PACKAGES");
  pricing.packages.forEach(p => {
    L.push("- " + p.id + " · " + p.name + " · " + packagePriceLabel(p) + " · up to " + p.onSiteHours + " hours on site");
    L.push("  Includes: " + p.allInclusions.join("; "));
    if (p.notIncluded) L.push("  Not included: " + p.notIncluded);
  });
  L.push("");
  L.push("HER ADD-ONS (fixed rates)");
  pricing.addons.forEach(a => L.push("- " + a.id + " · " + a.name + " · " + usd(a.rate) + " " + unitLabel(a.unit)
    + (a.includedIn && a.includedIn.length ? " (already included in: " + a.includedIn.join(", ") + ")" : "")));
  L.push("");
  L.push("CENTRAL COAST MARKET RATES (for custom extras and case-by-case fees)");
  L.push(marketRatesText(pricing.marketRates));
  L.push("");
  L.push("REQUESTED PACKAGE: " + (requestedPackageId || "none / help me choose"));
  const ids = (chosen && chosen.ids) || [];
  L.push("ADD-ONS THE COUPLE ALREADY CHOSE: " + (ids.length ? ids.join(", ") + " (these are included automatically; don't repeat them)" : "none"));
  L.push("");
  L.push("THE COUPLE");
  L.push(clientSummary);
  return { system: QUOTE_SYSTEM, prompt: L.join("\n") + "\n\nReturn strict JSON only." };
}

export function parseQuoteJSON(text) {
  if (!text || !text.trim()) throw new Error("The model returned an empty response. Try regenerating.");
  let s = text.trim();
  const fence = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
  if (fence) s = fence[1].trim();
  const a = s.indexOf("{"), b = s.lastIndexOf("}");
  if (a === -1 || b === -1) throw new Error("The model didn't return a quote in the expected format. Try regenerating.");
  try { return JSON.parse(s.slice(a, b + 1)); } catch (e) { throw new Error("Couldn't read the quote the model returned. Try regenerating."); }
}

const str = (v, max) => String(v == null ? "" : v).trim().slice(0, max || 300);

/**
 * Turns the model's choices into line items at her prices. Her prices always win:
 * the package and add-ons are priced here, not by the model. Each line has a `kind`
 * (package · addon · custom); the agreement's fee table is built from these lines.
 */
export function assembleQuote(raw, pricing, requestedPackageId, chosen) {
  const r = raw && typeof raw === "object" ? raw : {};
  const ids = pricing.packages.map(p => p.id);
  const pkgId = requestedPackageId && ids.includes(requestedPackageId) ? requestedPackageId
    : ids.includes(r.packageId) ? r.packageId : (ids.includes("partial") ? "partial" : ids[0]);
  const pkg = pricing.packages.find(p => p.id === pkgId);

  let pkgFee, pkgNote = "";
  if (pkg.price !== null && pkg.price !== undefined) {
    pkgFee = pkg.price;
  } else {
    const floor = pkg.priceFrom || 0;
    const asked = money(r.customFee !== undefined ? r.customFee : r.fullServiceFee);
    pkgFee = Math.max(floor, asked === null ? floor : asked);
    pkgNote = "Priced to your guest count, timeline and scope";
  }
  const lineItems = [{ label: pkg.name, amount: pkgFee, note: pkgNote, kind: "package" }];

  const seen = new Set();
  const addAddon = (id, qtyIn, note, suggested) => {
    const a = pricing.addons.find(ad => ad.id === id);
    if (!a || seen.has(a.id) || (a.includedIn || []).includes(pkg.id)) return;
    seen.add(a.id);
    const qty = a.unit === "flat" ? 1 : Math.min(40, Math.max(1, Math.round((Number(qtyIn) || 1) * 2) / 2));
    const label = a.name + (a.unit === "hour" ? " (" + qty + (qty === 1 ? " hour)" : " hours)") : a.unit === "person" && qty > 1 ? " (" + qty + ")" : "");
    lineItems.push({ label, amount: Math.round(a.rate * qty), note: str(note, 200), kind: "addon", addonId: a.id, qty, suggested: !!suggested });
  };
  const chosenIds = (chosen && Array.isArray(chosen.ids)) ? chosen.ids : [];
  const chosenQty = (chosen && chosen.qty) || {};
  chosenIds.forEach(id => addAddon(id, chosenQty[id], "", false));
  (Array.isArray(r.addons) ? r.addons : []).forEach(x => x && addAddon(x.id, x.qty, x.note, true));

  (Array.isArray(r.custom) ? r.custom : []).slice(0, 6).forEach(x => {
    const amount = money(x && x.amount);
    const label = str(x && x.label, 120);
    if (!label || amount === null || amount === 0) return;
    lineItems.push({ label, amount: Math.min(amount, 50000), note: str(x.note, 200), kind: "custom", market: str(x.market, 600) });
  });

  const base = lineItems.filter(li => li.kind !== "custom").reduce((s, li) => s + li.amount, 0);
  const all = lineItems.reduce((s, li) => s + li.amount, 0);
  return { packageId: pkg.id, packageTier: pkg.name, rangeLow: base, rangeHigh: all, lineItems, rationale: str(r.rationale, 1200) };
}
